import React from 'react';
import { Link, Navigate, useParams } from 'react-router-dom';
import { Box, Button, Container, Grid, Typography } from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { MODULE_CATALOG, isCardModuleEnabled, visibleLinksFor } from './moduleCatalog';
import { useAuth } from '../../context/AuthContext';
import { useModuleVisibility } from './useModuleVisibility';
import LauncherCard from './LauncherCard';

/** Help-mode page tag for each module landing (string literals, so the key-coverage test finds them). */
const LANDING_HELP: Record<string, Record<string, string>> = {
    accounting: { 'data-help-page': 'accounting.landing.page' },
    approvals: { 'data-help-page': 'approvals.landing.page' },
    attendance: { 'data-help-page': 'attendance.landing.page' },
    banking: { 'data-help-page': 'banking.landing.page' },
    commercial: { 'data-help-page': 'commercial.landing.page' },
    costing: { 'data-help-page': 'costing.landing.page' },
    crm: { 'data-help-page': 'crm.landing.page' },
    dashboards: { 'data-help-page': 'dashboards.landing.page' },
    expense: { 'data-help-page': 'expense.landing.page' },
    financing: { 'data-help-page': 'financing.landing.page' },
    fixedAssets: { 'data-help-page': 'fixedAssets.landing.page' },
    institutions: { 'data-help-page': 'institutions.landing.page' },
    items: { 'data-help-page': 'items.landing.page' },
    leave: { 'data-help-page': 'leave.landing.page' },
    maintenance: { 'data-help-page': 'maintenance.landing.page' },
    merchandising: { 'data-help-page': 'merchandising.landing.page' },
    notices: { 'data-help-page': 'notices.landing.page' },
    notifications: { 'data-help-page': 'notifications.landing.page' },
    payroll: { 'data-help-page': 'payroll.landing.page' },
    procurement: { 'data-help-page': 'procurement.landing.page' },
    reports: { 'data-help-page': 'reports.landing.page' },
    requisitions: { 'data-help-page': 'requisitions.landing.page' },
    saasOperator: { 'data-help-page': 'saasOperator.landing.page' },
    sales: { 'data-help-page': 'sales.landing.page' },
    shifts: { 'data-help-page': 'shifts.landing.page' },
    tasks: { 'data-help-page': 'tasks.landing.page' },
    tax: { 'data-help-page': 'tax.landing.page' },
};

/**
 * One module's pages as cards — the launcher equivalent of expanding a sidebar sub-menu.
 *
 * Both gates the sidebar applies are re-applied here: the module itself must be reachable and
 * enabled for the institution, and each page must pass its own gate. A module the user cannot
 * reach (typed URL, stale link) bounces back to the launcher rather than rendering an empty page.
 */
const ModuleDetailPage = () => {
    const { moduleKey } = useParams<{ moduleKey: string }>();
    const { permissionsReady } = useAuth();
    const { visibility, enabledModules } = useModuleVisibility();

    // On a hard reload the permission set is still loading on the first render, so every gate reads
    // false and a valid deep link (/modules/banking) would bounce to the launcher. Same wait as
    // PermissionRoute: decide once the effective set has loaded.
    if (!permissionsReady) {
        return null;
    }

    const card = MODULE_CATALOG.find(m => m.key === moduleKey);
    const reachable = !!card
        && visibility[card.key] === true
        && isCardModuleEnabled(card, enabledModules);

    const links = card && reachable ? visibleLinksFor(card, visibility) : [];

    if (!card || !reachable || links.length === 0) {
        return <Navigate to="/modules" replace />;
    }

    const Icon = card.icon;
    return (
        <Container maxWidth="lg" sx={{ py: 4 }} {...LANDING_HELP[card.key]}>
            <Button component={Link} to="/modules" startIcon={<ArrowBackIcon />} size="small" sx={{ mb: 2 }}>
                All Modules
            </Button>

            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 0.5 }}>
                <Icon color="primary" />
                <Typography variant="h5" fontWeight={600}>{card.label}</Typography>
            </Box>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                {card.blurb}
            </Typography>

            <Grid container spacing={2}>
                {links.map(link => (
                    <LauncherCard key={link.path} label={link.label} icon={link.icon} to={link.path} helpTag={link.helpKey} dense />
                ))}
            </Grid>
        </Container>
    );
};

export default ModuleDetailPage;
